import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { Button } from '~/components/primitives';
import { cancelEsf, fetchEsfDetail } from '~/lib/api/finance';
import { formatDate } from '~/lib/format';
import shared from './_auth._shared.module.css';

/**
 * S13-FE.5 — ESF document detail page.
 * FE-294 Pass 70: localize Status (raw `pushed_to_1c` → «Отправлен в 1С»),
 * Locale label + Approved by label через i18n (раньше hardcoded English).
 */

export const Route = createFileRoute('/_auth/finance/esf/$id')({ component: EsfDetailPage });

const STATE_LABELS: Record<string, () => string> = {
  draft: m.esf_state_draft,
  pending_approval: m.esf_state_pending_approval,
  approved: m.esf_state_approved,
  pushed_to_1c: m.esf_state_pushed_to_1c,
  rejected: m.esf_state_rejected,
  cancelled: m.esf_state_cancelled,
};

function Row({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div className={shared.detailRow}>
      <span className={shared.detailRowLabel}>{label}</span>
      <strong>{value ?? '—'}</strong>
    </div>
  );
}

function EsfDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['finance', 'esf', id], queryFn: () => fetchEsfDetail(id) });

  const cancelMut = useMutation({
    mutationFn: () => cancelEsf(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance', 'esf'] }),
  });

  const doc = query.data;
  if (!doc) {
    return (
      <div>
        <PageHeader title={m.phase2_finance_esf_title()} />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  // FE-104: business doc_no for H1 + breadcrumb (parity with list rendering).
  const docNoYear = doc.period?.date_from?.slice(0, 4) ?? '2026';
  const docNoSeq = doc.id.replace(/-/g, '').slice(-4).toUpperCase();
  const docNoPrefix = doc.kind === 'esf' ? 'ЭСФ' : 'КПН';
  const docNo = `${docNoPrefix}-${docNoYear}-${docNoSeq}`;

  // F90 (Ф6): отправка — заявкой уровня 3 с двумя подписями; кнопка видна, пока документ
  // можно отправить, а доступность (1С, роль, второй подписант) решает сервер.
  // С-G Р.5: кнопка не прячется — уже отправленный документ даёт причину на месте, прочие
  // состояния (отменён, отклонён) — код сервера из предпросмотра.
  const pushed = doc.state === 'pushed_to_1c';
  const canCancel = doc.state !== 'pushed_to_1c' && doc.state !== 'cancelled';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: m.phase2_finance_esf_title(), to: '/finance/esf' }, { label: docNo }]}
          />
        }
        title={`${m.phase2_finance_esf_title()} · ${docNo}`}
        actions={
          <>
            <Tier3SendAction
              blockedReason={pushed ? m.tier3_send_reason_already_pushed() : undefined}
              label={m.esf_button_push()}
              subject={{
                taskType: 'regulatory_push_esf',
                subjectKind: 'regulatory_doc',
                payload: { id },
              }}
            />
            {canCancel && (
              <Button
                variant="ghost"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
              >
                {m.invoice_button_cancel()}
              </Button>
            )}
          </>
        }
      />
      <PeriodCloseBanner scope="regulatory" />
      <div className={shared.detailCard}>
        <Row
          label={m.esf_column_state()}
          value={(STATE_LABELS[doc.state] ?? (() => doc.state))()}
        />
        <Row
          label={m.esf_column_period()}
          value={`${formatDate(doc.period.date_from)} — ${formatDate(doc.period.date_to)}`}
        />
        <Row label={m.esf_column_onec_ref()} value={doc.onec_doc_ref} />
        <Row label={m.esf_column_locale()} value={doc.locale} />
        <Row label={m.esf_column_approved_by()} value={doc.approved_by?.join(', ') || null} />
      </div>
    </div>
  );
}
